import * as React from 'react';
import Button from '@mui/material/Button';
import { useCallback, useEffect, useState } from 'react';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import { Alert, Backdrop, CircularProgress, Snackbar } from '@mui/material';
import {
  CardsContent,
  PageTitle,
  SideNavigation,
  TextFieldStyled,
} from './styles';
import { Company, Dashboard } from '../CompaniesPage';
import api from '../../services/api';
import UserGroupCard from '../../components/cards/usergroupCard';
import { OptionsBar } from '../CompaniesPage/styles';
import Modal from '../../components/modal';
import { useLogger } from '../../context/logContext';
import { Role } from '../AccessControlPage/roleTab';
import { DownloadItem } from '../../components/addUrlDownloads';
import { StoryUnit } from '../../components/addImagesStory';
import { Container, Card, CardContent, CardHeader } from '../../components/ui';

interface AlertProps {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

export interface Widget {
  name: string;
  widgetId: number;
  urlImage: string;
  description: string;
  url: string;
  type: string;
  downloadItens?: DownloadItem[];
  internalWidgetType?: string;
  children?: Widget[];
  stories?: StoryUnit[];
  spinradValues?: string[];
}

export interface WidgetNode extends Widget {
  items: WidgetNode[] | undefined;
}

export interface User {
  username: string;
  email: string;
}

export interface UserGroup {
  userGroupId: number;
  name: string;
  companyId: number;
  dashboards: number[];
  roles: number[];
  users: string[];
  widgets: string[];
  moreInformation: string[];
  settings: any;
}

const UserGroupsPage: React.FC = () => {
  const { logger } = useLogger();
  const [companiesLine, setCompaniesLine] = useState<any>();
  const [userGroupsPanel, setUserGroupsPanel] = useState<any>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [open, setOpen] = useState(false);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [filteredUserGroups, setFilteredUserGroups] = useState<UserGroup[]>([]);
  const [inputSearchValue, setInputSearchValue] = useState<string>();
  const [companyFilter, setCompanyFilter] = useState<number>();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [isloading, setIsLoading] = useState<boolean>(true);
  // const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [alertProps, setAlertProps] = useState<AlertProps>({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleFilter = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputSearchValue(event.target.value.toLowerCase());
    },
    [],
  );

  const getCompanies = useCallback(() => {
    api
      .get('/companies')
      .then((response: any) => {
        setCompanies(
          response.data.map((company: Company) => {
            return {
              ...company,
            };
          }),
        );
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, []);

  const getRoles = useCallback(() => {
    api
      .get('/roles')
      .then(response => {
        // remove the guest role to be selected on create usergroup
        const rolesNoGuest = response.data.filter(
          (role: any) => role.name !== 'Guest',
        );

        setRoles(rolesNoGuest);
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, []);

  const getUserGroups = useCallback((options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setIsLoading(true);
    api
      .get('/usergroups/getAll')
      .then((response: any) => {
        if (!silent) setIsLoading(false);
        setUserGroups(response.data?.usergroups);
        setFilteredUserGroups(response.data?.usergroups);

        setDashboards(
          response.data?.dashboardList.map((dash: any) => {
            return {
              dashboardName: dash.dashboard_title,
              dashboardId: dash.id,
            };
          }),
        );
      })
      .catch((err: any) => {
        if (!silent) setIsLoading(false);
        console.log(err);
      });
  }, []);

  useEffect(() => {
    getCompanies();
    getRoles();
    getUserGroups();
  }, [getCompanies, getRoles, getUserGroups]);

  useEffect(() => {
    let filtered = [...userGroups];
    if (companyFilter) {
      filtered = filtered.filter(
        (usergroup: UserGroup) => usergroup.companyId === companyFilter,
      );
    }
    if (inputSearchValue) {
      const inputValeuTam = inputSearchValue.length;
      filtered = filtered.filter((usergroup: UserGroup) => {
        const nFirstCharacters = usergroup.name.slice(0, inputValeuTam);
        return nFirstCharacters.toLowerCase() === inputSearchValue;
      });
    }

    setFilteredUserGroups(filtered);
  }, [companyFilter, inputSearchValue, userGroups]);

  const handleClickOnCompanyLeftSideLine = useCallback((companyId: number) => {
    setCompanyFilter(companyId);
  }, []);

  const handleSaveEdit = useCallback(
    (usergroupToEdit: any) => {
      api
        .put(`/usergroups/${usergroupToEdit.userGroupId}`, usergroupToEdit)
        .then(() => {
          getUserGroups({ silent: true });
        })
        .catch((error: any) => {
          console.log('err', error);
          logger('error', 'Edit UserGroup', `${error.code} - ${error.message}`);
        });
    },
    [getUserGroups, logger],
  );

  const handleDelete = useCallback(
    (obj: any) => {
      api
        .delete(`/usergroups/${obj.userGroup.userGroupId}`)
        .then(() => {
          getUserGroups({ silent: true });
        })
        .catch(error => {
          console.log('err', error);
          logger(
            'error',
            'Delete usergroups',
            `${error.code} - ${error.message}`,
          );
        });
      obj.handleCloseOptions();
    },
    [getUserGroups, logger],
  );

  const handleAdd = useCallback(
    (newUserGroup: UserGroup) => {
      api
        .post('/usergroups', newUserGroup)
        .then((res: any) => {
          if (res.data === 'usergroup already exists') {
            setAlertProps({
              open: true,
              message: res.data,
              severity: 'error',
            });
          } else {
            getUserGroups({ silent: true });
          }
        })
        .catch(error => {
          console.log('err', error);
          logger(
            'error',
            'create new usergroup',
            `${error.code} - ${error.message}`,
          );
        });
    },
    [getUserGroups, logger],
  );

  useEffect(() => {
    setCompaniesLine(
      companies
        .slice()
        .sort((a: Company, b: Company) =>
          a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }),
        )
        .map((company: Company, index: number) => {
          return (
            <div key={index}>
              <div style={{ height: '25px' }}></div>
              <Button
                key={index}
                style={{
                  left: '24px',
                  height: '60px',
                  borderBottomRightRadius: '50px',
                  borderTopRightRadius: '50px',
                }}
                variant="contained"
                onClick={() =>
                  handleClickOnCompanyLeftSideLine(company.companyId)
                }
              >
                {company.name}
              </Button>
            </div>
          );
        }),
    );
    setUserGroupsPanel(
      filteredUserGroups.map((userGroup: UserGroup, index: number) => {
        return (
          <div style={{ marginTop: '30px', height: '25%' }} key={index}>
            <UserGroupCard
              key={index}
              userGroup={userGroup}
              handleDelete={handleDelete}
              handleSaveEdit={handleSaveEdit}
              dashboardListForm={dashboards}
              rolesListForm={roles}
              companyListForm={companies}
            ></UserGroupCard>
          </div>
        );
      }),
    );
  }, [
    companies,
    dashboards,
    filteredUserGroups,
    handleClickOnCompanyLeftSideLine,
    handleDelete,
    handleSaveEdit,
    roles,
  ]);

  const handleSubmit = (): void => {
    setOpen(true);
  };

  const handleCloseAlert = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ): any => {
    if (reason === 'clickaway') {
      return;
    }

    setAlertProps({
      open: false,
      message: '',
      severity: 'info',
    });
  };

  return (
    <Container maxWidth="full" padding>
      <Card variant="elevated" padding="large" fullHeight>
        <CardHeader>
          <PageTitle>User groups</PageTitle>
        </CardHeader>
        <CardContent>
          <OptionsBar id="options">
            <div
              id="searchBar"
              style={{
                width: '50%',
                alignSelf: 'center',
                paddingTop: '10px',
              }}
            >
              <TextFieldStyled
                id="outlined-basic"
                label="Search..."
                onChange={handleFilter}
                variant="outlined"
              />
            </div>

            <div
              id="buttonAdd"
              style={{ alignSelf: 'center', paddingTop: '10px' }}
            >
              <Fab color="primary" aria-label="add" onClick={handleSubmit}>
                <AddIcon />
              </Fab>
            </div>
          </OptionsBar>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              height: '100%',
              paddingTop: '35px',
            }}
          >
            <SideNavigation id="navigation">{companiesLine}</SideNavigation>
            <CardsContent id="cards">{userGroupsPanel}</CardsContent>
          </div>

          <Modal
            content={'usergroups'}
            userGroupProp={{
              open,
              setOpen,
              dashboardListForm: dashboards,
              companyListForm: companies,
              rolesListForm: roles,
              handleAdd,
              getUserGroups,
            }}
          ></Modal>

          <Backdrop
            sx={theme => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
            open={isloading}
          >
            <CircularProgress color="inherit" />
            {/* <span style={{ marginLeft: '12px' }}>{loadingMessage}</span> */}
          </Backdrop>

          <Snackbar
            open={alertProps.open}
            autoHideDuration={6000}
            onClose={handleCloseAlert}
          >
            <Alert
              onClose={handleCloseAlert}
              severity={alertProps.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {alertProps.message}
            </Alert>
          </Snackbar>
        </CardContent>
      </Card>
    </Container>
  );
};

export default UserGroupsPage;
